// src/App.jsx
/**
 * Application entry point.
 *
 * Responsibilities
 * ────────────────
 *  • Mount the two possible nav variants (`<NavBar/> | <MDNavBar/>`).
 *  • Gate protected routes behind `<ProtectedRoute/>`.
 *  • Lazy-load page modules to keep initial bundle size low.
 *  • Redirect unmatched paths to `/`.
 *  • Run a quick device-capability check (`useDeviceCompatibility`). On
 *    unsupported devices we replace the `/auth/*` subtree with a
 *    compatibility-warning page.
 *
 * Layout skeleton
 *  <div class="h-[100dvh] flex flex-col">   ← fills viewport height
 *    {NavBar}
 *    <Suspense>  ← lazy pages rendered inside
 *       <Routes> … </Routes>
 *    </Suspense>
 *  </div>
 *
 * Notes
 *  • Use `100dvh` (dynamic viewport unit) so Safari iOS 15+ doesn’t crop
 *    the content when the URL bar shrinks.
 *  • `typography` Tailwind plugin class gives consistent heading / paragraph
 *    defaults across pages.
 */

import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import api from "./api/axiosInstance";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";
import useDeviceCompatibility from "./hooks/useDeviceCompatibility";

import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import HomePage from "./pages/HomePage";
import FormPage from "./pages/FormPage";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";
import QuestionnairesPage from "./pages/QuestionnairesPage";

import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth, onIdTokenChanged, getIdTokenResult } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { useAuth } from "./context/AuthContext";
import LevelSelectionPage from "./pages/LevelSelectionPage";
import Lvl1Router from "./routes/RecordingLvl1Router";
import ConfirmMetadataPage from "./pages/ConfirmMetadataPage";
import NewPatientPage from "./pages/NewPatientPage";
import PatientsProfilePage from "./pages/PatientsProfilePage";
import InvitePatientPage from "./pages/InvitePatientPage";
import CohortOverviewPage from "./pages/CohortOverviewPage";
import InstructionsPage from "./pages/InstructionsPage";
import PatientOverviewPage from "./pages/PatientOverviewPage";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const CompatibilityPage = lazy(() => import("./pages/CompatibilityPage"));
const PatientsPage = lazy(() => import("./pages/PatientsPage"));
/* ------------------------------------------------------------------ */

export default function App() {
  const auth = getAuth(firebaseApp);
  const storage = getStorage(firebaseApp);
  const { user } = useAuth(); // grab host’s user
  const [userData, setUserData] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  console.log(selectedPatient);

  const navigate = useNavigate();

  /* ----------------------------------------------------------------
   * 1.  keep `role` in state (null ⇢ "clinician" | "user")
   * -------------------------------------------------------------- */
  const [role, setRole] = useState(null);

  useEffect(() => {
    /* fires on first load **and** whenever Firebase refreshes the JWT */
    return onIdTokenChanged(getAuth(firebaseApp), async (u) => {
      if (!u) return setRole(null);
      try {
        const t = await getIdTokenResult(u);
        setRole((t.claims.role || "user").toLowerCase());
      } catch (e) {
        console.warn("[claims]", e);

        setRole("user");
      }
    });
  }, []);

  /* ----------------------------------------------------------------
   * 2.  once we know the user *is* a clinician, fetch MySQL metadata
   * -------------------------------------------------------------- */
  useEffect(() => {
    if (role !== "clinician" || !user) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/users-api/me", {
          params: { uid: user.uid },
        });

        console.log("users-api/me response", data);

        if (!cancelled) setUserData(data);
      } catch (err) {
        console.error("[users-api]", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role, user]);

  const [patientRows, setPatientRows] = useState(null);

  // fetch on mount / when clinic-id changes
  useEffect(() => {
    if (!userData?.clinicId) return; // nothing to do yet
    if (role !== "clinician" || !user) return;

    let cancelled = false; // local flag instead of `mounted`

    (async () => {
      try {
        const { data } = await api.get("/patients-api/patients", {
          params: { clinicId: userData?.clinicId },
        });

        if (!cancelled) setPatientRows(data); // update only if still mounted
      } catch (err) {
        if (!cancelled) console.error(err);
      }
    })();

    return () => {
      cancelled = true;
    }; // cleanup
  }, [userData?.clinicId]); // effect depends only on clinic-id

  // Feature-detect basic device capabilities (e.g., Web Audio, screen size)
  // const isCompatible = useDeviceCompatibility();

  // Decide which nav bar variant to render based on location auth state.
  const navVariant = useNavVariant(); // 'main' | 'landing'

  // if role has not been detected yet show a loading screen
  if (role === null && user) {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  const isClinician = role === "clinician";

  return (
    <div className="h-[100dvh] flex flex-col typography relative">
      {navVariant === "main" ? <NavBar /> : <MDNavBar />}

      {/* Suspense boundary – shows fallback while lazy chunks download */}
      <div className="flex flex-col items-center justify-start h-full overflow-y-scroll">
        <Suspense fallback={<div className="m-auto">Loading…</div>}>
          <Routes>
            {/* ---------- Public routes ---------- */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/instructions" element={<InstructionsPage />} />
            <Route
              path="/auth/*"
              element={<AuthPage />}
              // element={isCompatible ? <AuthPage /> : <CompatibilityPage />}
            />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordRequest />}
            />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* ---------- Protected routes ---------- */}
            {isClinician ? (
              <>
                <Route
                  path="/patients"
                  element={
                    <ProtectedRoute>
                      <PatientsPage
                        userData={userData}
                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                        rows={patientRows}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/patients/new"
                  element={
                    <ProtectedRoute>
                      <NewPatientPage userData={userData} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/patients/invite/*"
                  element={
                    <ProtectedRoute>
                      <InvitePatientPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/cohort-overview"
                  element={
                    <ProtectedRoute>
                      <CohortOverviewPage rows={patientRows} />
                    </ProtectedRoute>
                  }
                />
              </>
            ) : (
              <>
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/questionnaires/:questionnaireName?"
                  element={
                    <ProtectedRoute>
                      <QuestionnairesPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            {isClinician && !selectedPatient ? (
              <></>
            ) : (
              <>
                <Route
                  path="/recording"
                  element={
                    <ProtectedRoute>
                      <LevelSelectionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recording/lvl1/*"
                  element={
                    <ProtectedRoute>
                      <Lvl1Router
                        auth={auth}
                        storage={storage}
                        user={
                          isClinician && selectedPatient
                            ? selectedPatient
                            : user
                        }
                        takenBy={user}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/patients/profile"
                  element={
                    <ProtectedRoute>
                      <PatientsProfilePage
                        userData={userData}
                        selectedPatient={selectedPatient}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/patients/overview"
                  element={
                    <ProtectedRoute>
                      <PatientOverviewPage
                        rows={patientRows}
                        selectedPatient={selectedPatient}
                        setSelectedPatient={setSelectedPatient}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/questionnaires/:questionnaireName?"
                  element={
                    <ProtectedRoute>
                      <QuestionnairesPage selectedPatient={selectedPatient} />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            <Route
              path="/form"
              element={
                <ProtectedRoute>
                  <FormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/review-metadata"
              element={
                <ProtectedRoute>
                  <ConfirmMetadataPage />
                </ProtectedRoute>
              }
            />

            {/* ---------- Fallback ---------- */}
            <Route
              path="*"
              element={
                <Navigate to={isClinician ? "/patients" : "/"} replace />
              }
            />
          </Routes>
        </Suspense>
      </div>
      {/* {selectedPatient && (
        <p className="muted-text absolute left-0 top-0 ">
          PID: {selectedPatient.alias}
        </p>
      )} */}
    </div>
  );
}
